var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faLongArrowAltLeft, faLongArrowAltRight, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { refDebounced } from "@vueuse/core";
import { BButton, BFormInput } from "bootstrap-vue";
import { computed, nextTick, reactive, ref } from "vue";
import { useUid } from "@/composables/utils/uid";
import { useHighlight } from "./useHighlight";
import { filterOptions } from "./worker/filterOptions";
import { useSelectMany } from "./worker/selectMany";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FormSelectMany',
    props: {
        id: { type: String, default: () => useUid("form-select-many-").value },
        disabled: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            required: true,
        },
        placeholder: {
            type: String,
            default: "Search for options",
        },
        value: {
            type: Array,
            default: null,
        },
    },
    emits: ["input"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faLongArrowAltLeft, faLongArrowAltRight, faTimes);
        const searchValue = ref("");
        const useRegex = ref(false);
        const caseSensitive = ref(false);
        const searchRegex = computed(() => {
            if (useRegex.value) {
                try {
                    const regex = new RegExp(searchValue.value, caseSensitive.value ? undefined : "i");
                    return regex;
                }
                catch (e) {
                    return null;
                }
            }
            else {
                return null;
            }
        });
        /** Wraps value prop so it can be set, and always returns an array */
        const selected = computed({
            get() {
                if (props.value === null) {
                    return [];
                }
                else if (Array.isArray(props.value)) {
                    return props.value;
                }
                else {
                    return [props.value];
                }
            },
            set(value) {
                emit("input", value);
            },
        });
        const regexInvalid = computed(() => useRegex.value && searchRegex.value === null);
        // Limits amount of displayed options
        const selectedDisplayCount = ref(1000);
        const unselectedDisplayCount = ref(1000);
        const filter = computed(() => { var _a; return (_a = searchRegex.value) !== null && _a !== void 0 ? _a : searchValue.value; });
        // binding to worker thread
        const { unselectedOptionsFiltered, selectedOptionsFiltered, running, moreUnselected, moreSelected } = useSelectMany({
            optionsArray: computed(() => props.options),
            filter,
            selected,
            selectedDisplayCount,
            unselectedDisplayCount,
            caseSensitive,
        });
        // debounced to it doesn't blink, and only appears when relevant
        const workerRunning = refDebounced(running, 400);
        /** generic event handler to handle highlighting of options */
        function handleHighlight(event, index, highlightHandler) {
            if (event.shiftKey && event.ctrlKey) {
                highlightHandler.rangeRemoveHighlight(index);
            }
            else if (event.shiftKey) {
                highlightHandler.rangeHighlight(index);
            }
            else if (event.ctrlKey) {
                highlightHandler.toggleHighlight(index);
            }
        }
        /** focus option at given index, or previous option if that doesn't exist */
        function focusOptionAtIndex(selected, index) {
            var _a;
            const el = document.getElementById(`${props.id}-${selected}-${index}`);
            if (el) {
                el.focus();
            }
            else {
                (_a = document.getElementById(`${props.id}-${selected}-${index - 1}`)) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
        function selectOption(event, index) {
            return __awaiter(this, void 0, void 0, function* () {
                if (event.shiftKey || event.ctrlKey) {
                    handleHighlight(event, index, highlightUnselected);
                }
                else {
                    const [option] = unselectedOptionsFiltered.value.splice(index, 1);
                    if (option) {
                        selected.value = [...selected.value, option.value];
                    }
                    // select the element which now is where the removed element just was
                    // to improve keyboard navigation
                    yield nextTick();
                    focusOptionAtIndex("unselected", index);
                }
            });
        }
        function deselectOption(event, index) {
            return __awaiter(this, void 0, void 0, function* () {
                if (event.shiftKey || event.ctrlKey) {
                    handleHighlight(event, index, highlightSelected);
                }
                else {
                    const [option] = selectedOptionsFiltered.value.splice(index, 1);
                    if (option) {
                        const i = selected.value.findIndex((selectedValue) => {
                            var _a;
                            if (typeof selectedValue === "string") {
                                return selectedValue === option.value;
                            }
                            else if (typeof selectedValue === "object" && typeof option.value === "object") {
                                // in case values are objects, compare their ids (if they have the 'id' property)
                                return (selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.id) === ((_a = option.value) === null || _a === void 0 ? void 0 : _a.id);
                            }
                            return false;
                        });
                        selected.value = selected.value.flatMap((value, index) => (index === i ? [] : [value]));
                    }
                    yield nextTick();
                    focusOptionAtIndex("selected", index);
                }
            });
        }
        function selectAll() {
            if (highlightUnselected.highlightedIndexes.length > 0) {
                const highlightedValues = highlightUnselected.highlightedOptions.map((o) => o.value);
                const selectedSet = new Set([...selected.value, ...highlightedValues]);
                selected.value = Array.from(selectedSet);
                unselectedOptionsFiltered.value.filter((o) => highlightedValues.includes(o.value));
            }
            else if (searchValue.value === "") {
                selected.value = props.options.map((o) => o.value);
                unselectedOptionsFiltered.value = [];
            }
            else {
                const filteredValues = filterOptions(props.options, filter.value, caseSensitive.value).map((o) => o.value);
                const selectedSet = new Set([...selected.value, ...filteredValues]);
                selected.value = Array.from(selectedSet);
                unselectedOptionsFiltered.value = [];
            }
        }
        function deselectAll() {
            if (highlightSelected.highlightedIndexes.length > 0) {
                const selectedSet = new Set(selected.value);
                const highlightedValues = highlightSelected.highlightedOptions.map((o) => o.value);
                highlightedValues.forEach((v) => selectedSet.delete(v));
                selected.value = Array.from(selectedSet);
                selectedOptionsFiltered.value.filter((o) => highlightedValues.includes(o.value));
            }
            else if (searchValue.value === "") {
                selected.value = [];
                selectedOptionsFiltered.value = [];
            }
            else {
                const selectedSet = new Set(selected.value);
                const filteredValues = filterOptions(props.options, filter.value, caseSensitive.value).map((o) => o.value);
                filteredValues.forEach((v) => selectedSet.delete(v));
                selected.value = Array.from(selectedSet);
                selectedOptionsFiltered.value = [];
            }
        }
        function optionOnKey(selected, event, index) {
            var _a;
            // handle highlighting
            if ([" ", "Enter"].includes(event.key) && (event.shiftKey || event.ctrlKey)) {
                const highlightHandler = selected === "selected" ? highlightSelected : highlightUnselected;
                handleHighlight(event, index, highlightHandler);
                event.preventDefault();
                return;
            }
            if (!["ArrowUp", "ArrowDown"].includes(event.key)) {
                return;
            }
            event.preventDefault();
            // handle arrow navigation
            const nextIndex = event.key === "ArrowUp" ? index - 1 : index + 1;
            (_a = document.getElementById(`${props.id}-${selected}-${nextIndex}`)) === null || _a === void 0 ? void 0 : _a.focus();
        }
        const highlightUnselected = reactive(useHighlight(unselectedOptionsFiltered));
        const highlightSelected = reactive(useHighlight(selectedOptionsFiltered));
        const selectText = computed(() => {
            if (highlightUnselected.highlightedIndexes.length > 0) {
                return "Select highlighted";
            }
            else if (searchValue.value === "") {
                return "Select all";
            }
            else {
                return "Select filtered";
            }
        });
        const deselectText = computed(() => {
            if (highlightSelected.highlightedIndexes.length > 0) {
                return "Deselect highlighted";
            }
            else if (searchValue.value === "") {
                return "Deselect all";
            }
            else {
                return "Deselect filtered";
            }
        });
        const unselectedCount = computed(() => {
            if (searchValue.value === "") {
                return `${props.options.length - selected.value.length}`;
            }
            else {
                let countString = `${unselectedOptionsFiltered.value.length}`;
                if (moreUnselected.value) {
                    countString += "+";
                }
                return countString;
            }
        });
        const selectedCount = computed(() => {
            if (searchValue.value === "") {
                return `${selected.value.length}`;
            }
            else {
                let countString = `${selectedOptionsFiltered.value.length}`;
                if (moreSelected.value) {
                    countString += "+";
                }
                return countString;
            }
        });
        return { __sfc: true, props, emit, searchValue, useRegex, caseSensitive, searchRegex, selected, regexInvalid, selectedDisplayCount, unselectedDisplayCount, filter, unselectedOptionsFiltered, selectedOptionsFiltered, running, moreUnselected, moreSelected, workerRunning, handleHighlight, focusOptionAtIndex, selectOption, deselectOption, selectAll, deselectAll, optionOnKey, highlightUnselected, highlightSelected, selectText, deselectText, unselectedCount, selectedCount, FontAwesomeIcon, BButton, BFormInput };
    }
});
